import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../helpers/authContext';
import { useToast } from 'components/ui/use-toast';
import { isLoggedWithKeychain, numberWithCommas } from 'services/dsteem-service';
import { extractSessionData } from 'helpers/localStorage';
import {
    getMarketTicker,
    getOrderBook,
    getRecentTrades,
    getOpenOrders,
    placeLimitOrderWithWif,
    cancelLimitOrderWithWif,
    parseAmt,
    roundNum,
    fmtDate,
    getExpiration,
    processTicker,
    processOrderbook,
    processTrades,
    processOpenOrders
} from 'helpers/steem';
import {
    placeLimitOrderKeychain,
    cancelLimitOrderKeychain
} from 'helpers/keychain';
import LoginModal from './userModals/LoginModal';
import useModal from 'helpers/useModal';
import { Button } from 'reactstrap';

const POLL_INTERVAL = 6000;

type Side = 'buy' | 'sell';

interface TickerData {
    latest: number;
    lowest_ask: number;
    highest_bid: number;
    percent_change: number;
    steem_volume: number;
    sbd_volume: number;
}

interface OrderRow {
    price: number;
    steem: number;
    sbd: number;
    side: 'bids' | 'asks';
}

interface Trade {
    date: string;
    side: 'buy' | 'sell';
    price: number;
    steem: number;
    sbd: number;
}

interface OpenOrder {
    orderid: number;
    created: string;
    side: 'buy' | 'sell';
    price: number;
    steem: number;
    sbd: number;
}

interface UserMarketTabProps {
    steemBalance?: number;
    sbdBalance?: number;
    onBalanceRefresh?: () => void;
}

const UserMarketTab: React.FC<UserMarketTabProps> = ({ 
    steemBalance = 0, 
    sbdBalance = 0, 
    onBalanceRefresh 
}) => {
    const { user, autologin } = useAuth();
    const { toast } = useToast();
    const { isOpenLoginModal, toggleLoginModal } = useModal();
    const keychainLogin = isLoggedWithKeychain(autologin);

    const [ticker, setTicker] = useState<TickerData | null>(null);
    const [orderbook, setOrderbook] = useState<{ bids: OrderRow[]; asks: OrderRow[] }>({ bids: [], asks: [] });
    const [trades, setTrades] = useState<Trade[]>([]);
    const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);

    const [activeSide, setActiveSide] = useState<Side>('buy');
    const [price, setPrice] = useState('');
    const [amount, setAmount] = useState('');
    const [total, setTotal] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCanceling, setIsCanceling] = useState<number | null>(null);
    const [pendingWif, setPendingWif] = useState<any>(null);

    const pollingRef = useRef<any>(null);

    const fetchAll = useCallback(async () => {
        try {
            const [tickerRaw, orderbookRaw, tradesRaw] = await Promise.all([
                getMarketTicker(),
                getOrderBook(15),
                getRecentTrades(25),
            ]);
            
            const processedTicker = processTicker(tickerRaw);
            if (processedTicker) {
                setTicker({
                    ...processedTicker,
                    steem_volume: parseAmt(tickerRaw.steem_volume) || 0,
                    sbd_volume: parseAmt(tickerRaw.sbd_volume) || 0,
                });
            }
            
            setOrderbook(processOrderbook(orderbookRaw));
            setTrades(processTrades(tradesRaw));
        } catch (e) {
            console.error('Market fetch error:', e);
        }
    }, []);

    const fetchOpenOrders = useCallback(async () => {
        if (!user) return;
        try {
            const raw = await getOpenOrders(user);
            setOpenOrders(processOpenOrders(raw));
        } catch (e) {}
    }, [user]);

    useEffect(() => {
        fetchAll();
        if (user) fetchOpenOrders();
        pollingRef.current = setInterval(() => {
            fetchAll();
            if (user) fetchOpenOrders();
        }, POLL_INTERVAL);
        return () => clearInterval(pollingRef.current);
    }, [fetchAll, fetchOpenOrders, user]);

    // Auto-calculate total from price × amount
    const handlePriceChange = (v: string) => {
        setPrice(v);
        const p = parseFloat(v);
        const a = parseFloat(amount);
        if (!isNaN(p) && !isNaN(a)) setTotal(roundNum(p * a, 3).toString());
    };
    const handleAmountChange = (v: string) => {
        setAmount(v);
        const p = parseFloat(price);
        const a = parseFloat(v);
        if (!isNaN(p) && !isNaN(a)) setTotal(roundNum(p * a, 3).toString());
    };
    const handleTotalChange = (v: string) => {
        setTotal(v);
        const p = parseFloat(price);
        const t = parseFloat(v);
        if (!isNaN(p) && p > 0 && !isNaN(t)) setAmount(roundNum(t / p, 3).toString());
    };

    const fillFromOrderbook = (row: OrderRow) => {
        setPrice(row.price.toFixed(6));
        setAmount(row.steem.toFixed(3));
        setTotal(row.sbd.toFixed(3));
    };

    const handleSubmitOrder = async (wifInput?: string) => {
        const p = parseFloat(price);
        const a = parseFloat(amount);
        const t = parseFloat(total);
        if (!p || !a || !t || p <= 0 || a <= 0 || t <= 0) {
            toast({ title: 'Fill all fields', description: 'Enter price, amount and total.', variant: 'rose_red' });
            return;
        }

        let currentWif = wifInput;
        if (!currentWif && !keychainLogin && autologin) {
            const session = extractSessionData(autologin);
            if (session && session[1]) {
                currentWif = session[1];
            }
        }

        if (!currentWif && !keychainLogin) {
            setPendingWif({ action: 'place' });
            toggleLoginModal();
            return;
        }

        setIsSubmitting(true);
        const orderid = Math.floor(Date.now() / 1000);
        let res: any;
        if (activeSide === 'buy') {
            const sell = `${t.toFixed(3)} SBD`;
            const receive = `${a.toFixed(3)} STEEM`;
            if (keychainLogin) {
                res = await placeLimitOrderKeychain(user, orderid, sell, receive, false, getExpiration());
            } else {
                res = await placeLimitOrderWithWif(user, currentWif!, orderid, sell, receive);
            }
        } else {
            const sell = `${a.toFixed(3)} STEEM`;
            const receive = `${t.toFixed(3)} SBD`;
            if (keychainLogin) {
                res = await placeLimitOrderKeychain(user, orderid, sell, receive, false, getExpiration());
            } else {
                res = await placeLimitOrderWithWif(user, currentWif!, orderid, sell, receive);
            }
        }
        setIsSubmitting(false);

        if (res?.success) {
            toast({ title: 'Order placed!', description: `Your ${activeSide} order was placed successfully.`, variant: 'wild_green' });
            setPrice(''); setAmount(''); setTotal('');
            if (onBalanceRefresh) onBalanceRefresh();
            setTimeout(() => { fetchAll(); fetchOpenOrders(); }, 3000);
        } else {
            if (res?.message?.includes('Missing Active')) {
                setPendingWif({ action: 'place' });
                toggleLoginModal();
            } else {
                toast({ title: 'Order failed', description: res?.message || 'Unknown error.', variant: 'rose_red' });
            }
        }
    };

    const handleCancelOrder = async (orderid: number, wifInput?: string) => {
        let currentWif = wifInput;
        if (!currentWif && !keychainLogin && autologin) {
            const session = extractSessionData(autologin);
            if (session && session[1]) {
                currentWif = session[1];
            }
        }

        if (!currentWif && !keychainLogin) {
            setPendingWif({ action: 'cancel', orderid });
            toggleLoginModal();
            return;
        }

        setIsCanceling(orderid);
        let res: any;
        if (keychainLogin) {
            res = await cancelLimitOrderKeychain(user, orderid);
        } else {
            res = await cancelLimitOrderWithWif(user, currentWif!, orderid);
        }
        setIsCanceling(null);

        if (res?.success) {
            toast({ title: 'Order cancelled', variant: 'wild_green', description: 'Your order was removed from the book.' });
            if (onBalanceRefresh) onBalanceRefresh();
            setTimeout(() => fetchOpenOrders(), 2000);
        } else {
            if (res?.message?.includes('Missing Active')) {
                setPendingWif({ action: 'cancel', orderid });
                toggleLoginModal();
            } else {
                toast({ title: 'Cancel failed', description: res?.message || 'Unknown error.', variant: 'rose_red' });
            }
        }
    };

    const spreadPct = ticker && ticker.highest_bid > 0
        ? ((200 * (ticker.lowest_ask - ticker.highest_bid)) / (ticker.highest_bid + ticker.lowest_ask)).toFixed(3)
        : '—';

    const maxBidDepth = orderbook.bids.length ? Math.max(...orderbook.bids.map(o => o.sbd)) : 1;
    const maxAskDepth = orderbook.asks.length ? Math.max(...orderbook.asks.map(o => o.sbd)) : 1;

    return (
        <div className="market-tab-wrapper">
            <div className="market-ticker-bar">
                <div className="market-ticker-pair">
                    <img src="/assets/img/steem.webp" className="market-pair-icon" alt="STEEM" />
                    <img src="/assets/img/sbd.webp" className="market-pair-icon market-pair-icon-2" alt="SBD" />
                    <span className="market-pair-label">STEEM/SBD</span>
                </div>
                {ticker ? (
                    <>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">Last Price</span>
                            <span className={`market-ticker-value ${ticker.percent_change >= 0 ? 'up' : 'down'}`}>
                                {ticker.latest.toFixed(6)}
                            </span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">24h Change</span>
                            <span className={`market-ticker-value ${ticker.percent_change >= 0 ? 'up' : 'down'}`}>
                                {ticker.percent_change >= 0 ? '+' : ''}{ticker.percent_change.toFixed(2)}%
                            </span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">Bid</span>
                            <span className="market-ticker-value up">{ticker.highest_bid.toFixed(6)}</span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">Ask</span>
                            <span className="market-ticker-value down">{ticker.lowest_ask.toFixed(6)}</span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">Spread</span>
                            <span className="market-ticker-value text-muted">{spreadPct}%</span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">24h Vol (STEEM)</span>
                            <span className="market-ticker-value">{numberWithCommas(ticker.steem_volume.toFixed(0))}</span>
                        </div>
                        <div className="market-ticker-item">
                            <span className="market-ticker-label">24h Vol (SBD)</span>
                            <span className="market-ticker-value">{numberWithCommas(ticker.sbd_volume.toFixed(0))}</span>
                        </div>
                    </>
                ) : (
                    <span className="spinner dark" style={{ margin: 'auto' }} />
                )}
            </div>

            <div className="market-main-grid">
                <div className="market-orderbook">
                    <div className="market-panel-header">Order Book</div>
                    <div className="market-ob-header">
                        <span>Price (SBD)</span>
                        <span>Amount (STEEM)</span>
                        <span>Total (SBD)</span>
                    </div>
                    <div className="market-ob-asks">
                        {orderbook.asks.map((row, i) => (
                            <div key={i} className="market-ob-row ask" onClick={() => fillFromOrderbook(row)}>
                                <div className="market-ob-depth-bar" style={{ width: `${(row.sbd / maxAskDepth) * 100}%` }} />
                                <span className="market-ob-price down ob-price">{row.price.toFixed(6)}</span>
                                <span className="ob-steem">{row.steem.toFixed(3)}</span>
                                <span className="ob-sbd">{row.sbd.toFixed(3)}</span>
                            </div>
                        ))}
                    </div>
                    {ticker && (
                        <div className={`market-ob-last-price ${ticker.percent_change >= 0 ? 'up' : 'down'}`}>
                            <i className={`fas fa-${ticker.percent_change >= 0 ? 'arrow-up' : 'arrow-down'} mr-1`} />
                            {ticker.latest.toFixed(6)}
                            <span className="text-muted ml-2" style={{ fontSize: '11px' }}>= {ticker.latest.toFixed(6)} SBD</span>
                        </div>
                    )}
                    <div className="market-ob-bids">
                        {orderbook.bids.map((row, i) => (
                            <div key={i} className="market-ob-row bid" onClick={() => fillFromOrderbook(row)}>
                                <div className="market-ob-depth-bar bid-bar" style={{ width: `${(row.sbd / maxBidDepth) * 100}%` }} />
                                <span className="market-ob-price up ob-price">{row.price.toFixed(6)}</span>
                                <span className="ob-steem">{row.steem.toFixed(3)}</span>
                                <span className="ob-sbd">{row.sbd.toFixed(3)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="market-form-panel">
                    <div className="market-form-tabs">
                        <button
                            className={`market-form-tab buy ${activeSide === 'buy' ? 'active' : ''}`}
                            onClick={() => setActiveSide('buy')}
                        >Buy STEEM</button>
                        <button
                            className={`market-form-tab sell ${activeSide === 'sell' ? 'active' : ''}`}
                            onClick={() => setActiveSide('sell')}
                        >Sell STEEM</button>
                    </div>

                    <div className="market-form-body">
                        {ticker && (
                            <div className={`market-form-ref-price ${activeSide === 'buy' ? 'down' : 'up'}`}>
                                {activeSide === 'buy' ? `Best Ask: ${ticker.lowest_ask.toFixed(6)} SBD` : `Best Bid: ${ticker.highest_bid.toFixed(6)} SBD`}
                                <button className="btn-link ml-2" onClick={() => {
                                    const p = activeSide === 'buy' ? ticker.lowest_ask : ticker.highest_bid;
                                    setPrice(p.toFixed(6));
                                    const a = parseFloat(amount);
                                    if (!isNaN(a)) setTotal(roundNum(p * a, 3).toString());
                                }}>Fill</button>
                            </div>
                        )}
                        <div className="market-form-group">
                            <label>Price</label>
                            <div className="market-form-input-wrap">
                                <input
                                    type="number"
                                    className="form-control fancy-input"
                                    placeholder="0.000000"
                                    value={price}
                                    onChange={e => handlePriceChange(e.target.value)}
                                    step="0.000001"
                                    min="0"
                                />
                                <span className="market-form-badge">SBD</span>
                            </div>
                        </div>
                        <div className="market-form-group">
                            <label>Amount</label>
                            <div className="market-form-input-wrap">
                                <input
                                    type="number"
                                    className="form-control fancy-input"
                                    placeholder="0.000"
                                    value={amount}
                                    onChange={e => handleAmountChange(e.target.value)}
                                    step="0.001"
                                    min="0"
                                />
                                <span className="market-form-badge">STEEM</span>
                            </div>
                        </div>
                        <div className="market-form-balance-hint">
                            <i className="fas fa-wallet mr-1" />Available: <strong>{activeSide === 'buy' ? sbdBalance.toFixed(3) : steemBalance.toFixed(3)} {activeSide === 'buy' ? 'SBD' : 'STEEM'}</strong>
                        </div>
                        <div className="market-form-pct-row">
                            {[25, 50, 75, 100].map(pct => (
                                <button key={pct} className="market-pct-btn" onClick={() => {
                                    const p = parseFloat(price);
                                    if (activeSide === 'buy') {
                                        const t = roundNum((sbdBalance * pct) / 100, 3);
                                        setTotal(t.toString());
                                        if (!isNaN(p) && p > 0) setAmount(roundNum(t / p, 3).toString());
                                    } else {
                                        const a = roundNum((steemBalance * pct) / 100, 3);
                                        setAmount(a.toString());
                                        if (!isNaN(p) && p > 0) setTotal(roundNum(p * a, 3).toString());
                                    }
                                }}>{pct}%</button>
                            ))}
                        </div>
                        <div className="market-form-group">
                            <label>Total</label>
                            <div className="market-form-input-wrap">
                                <input
                                    type="number"
                                    className="form-control fancy-input"
                                    placeholder="0.000"
                                    value={total}
                                    onChange={e => handleTotalChange(e.target.value)}
                                    step="0.001"
                                    min="0"
                                />
                                <span className="market-form-badge">SBD</span>
                            </div>
                        </div>

                        {user ? (
                            <Button
                                className={`w-100 btn-fancy market-submit-btn ${activeSide}`}
                                disabled={isSubmitting}
                                onClick={() => handleSubmitOrder()}
                            >
                                {isSubmitting ? <span className="spinner dark m-0" /> : (
                                    <>
                                        <i className={`fas fa-${activeSide === 'buy' ? 'cart-shopping' : 'arrow-up-from-bracket'} mr-2`} />
                                        {activeSide === 'buy' ? 'Buy STEEM' : 'Sell STEEM'}
                                        {keychainLogin && (
                                            <img className="icon icon-sm ml-1" src="/assets/img/keychain.svg" />
                                        )}
                                    </>
                                )}
                            </Button>
                        ) : (
                            <div className="market-login-prompt">
                                <i className="fas fa-lock mr-2" />Log in to trade
                            </div>
                        )}
                    </div>
                </div>

                <div className="market-trades-panel">
                    <div className="market-panel-header">Recent Trades</div>
                    <div className="market-ob-header">
                        <span>Price (SBD)</span>
                        <span>STEEM</span>
                        <span>Time</span>
                    </div>
                    <div className="market-trades-list">
                        {trades.map((t, i) => (
                            <div key={i} className="market-ob-row">
                                <span className={`market-ob-price ${t.side === 'buy' ? 'up' : 'down'}`}>{t.price.toFixed(6)}</span>
                                <span>{t.steem.toFixed(3)}</span>
                                <span className="text-muted">{fmtDate(t.date)}</span>
                            </div>
                        ))}
                        {!trades.length && <div className="market-empty">Loading trades…</div>}
                    </div>
                </div>
            </div>

            {user && (
                <div className="market-open-orders">
                    <div className="market-panel-header">
                        <i className="fas fa-list-check mr-2" />My Open Orders
                        <button className="btn-link ml-2 small" onClick={fetchOpenOrders}>
                            <i className="fas fa-rotate-right" />
                        </button>
                    </div>
                    {openOrders.length === 0 ? (
                        <div className="market-empty">No open orders.</div>
                    ) : (
                        <div className="market-orders-table-wrap">
                            <table className="market-orders-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Price (SBD)</th>
                                        <th>STEEM</th>
                                        <th>SBD</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {openOrders.map(o => (
                                        <tr key={o.orderid}>
                                            <td className="text-muted">{fmtDate(o.created)}</td>
                                            <td className={o.side === 'buy' ? 'up' : 'down'}>
                                                {o.side === 'buy' ? 'Buy' : 'Sell'}
                                            </td>
                                            <td>{o.price.toFixed(6)}</td>
                                            <td>{o.steem.toFixed(3)}</td>
                                            <td>{o.sbd.toFixed(3)}</td>
                                            <td>
                                                <button
                                                    className="market-cancel-btn"
                                                    disabled={isCanceling === o.orderid}
                                                    onClick={() => handleCancelOrder(o.orderid)}
                                                >
                                                    {isCanceling === o.orderid
                                                        ? <span className="spinner dark m-0" style={{ width: 14, height: 14 }} />
                                                        : <><i className="fas fa-xmark mr-1" />Cancel</>
                                                    }
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <LoginModal
                isShowing={isOpenLoginModal}
                hide={toggleLoginModal}
                requiredActive={true}
                onSuccess={({ wif: wifInput }: any) => {
                    if (!wifInput || !pendingWif) return;
                    if (pendingWif.action === 'place') {
                        handleSubmitOrder(wifInput);
                    } else if (pendingWif.action === 'cancel') {
                        handleCancelOrder(pendingWif.orderid, wifInput);
                    }
                    setPendingWif(null);
                }}
            />
        </div>
    );
};

export default UserMarketTab;
